// LedgerStats.jsx
import React, { useMemo, useState } from "react";
import { getAmountInCurrencyFormat, limitNumber } from "../../utils/number";

/* ─────────────────────────────────────────
   computeStats
   
   Cash logic:
   - Charges are ADDED to cash receivable from the customer.
     i.e. if customer owes us cash AND owes us charges, both are receivable.
   - cashBalance = (rxCash - gvCash) + totalCharges - totalPaid
     where totalCharges = all section charges (receive + given side, since
     both are imposed on the customer)
   
   isDailyReport: excludes rwg/rws amounts from charges
───────────────────────────────────────── */
function computeStats(receiveRows, givenRows, isDailyReport) {
  const sum = (rows, fn) => rows.reduce((acc, r) => acc + (fn(r) || 0), 0);

  // ── Gold (pc + lab + bar + coin + ginni + rwg weight) ──
  const goldSections = ["pc", "lab", "bar", "coin", "ginni"];
  const rxGold =
    goldSections.reduce(
      (acc, k) => acc + sum(receiveRows, (r) => r[k]?.weight),
      0,
    ) + sum(receiveRows, (r) => r.rwg?.weight);
  const gvGold =
    goldSections.reduce(
      (acc, k) => acc + sum(givenRows, (r) => r[k]?.weight),
      0,
    ) + sum(givenRows, (r) => r.rwg?.weight);
  const goldBalance = rxGold - gvGold;

  // ── Silver (silver weight + rws weight) ──
  const rxSilver =
    sum(receiveRows, (r) => r.silver?.weight) +
    sum(receiveRows, (r) => r.rws?.weight);
  const gvSilver =
    sum(givenRows, (r) => r.silver?.weight) +
    sum(givenRows, (r) => r.rws?.weight);
  const silverBalance = rxSilver - gvSilver;

  // ── Raw cash (without charges) ──
  const rxCash = sum(receiveRows, (r) => r.cash);
  const gvCash = sum(givenRows, (r) => r.cash);
  const rawCashBalance = rxCash - gvCash;

  // ── Charges (imposed on the customer — both receive & given sides) ──
  const normalChargeSections = ["pc", "bar", "coin", "silver"];
  const specialChargeSections = ["lab", "ginni"];

  const rxNormalCharges =
    normalChargeSections.reduce(
      (acc, k) => acc + sum(receiveRows, (r) => r[k]?.charges),
      0,
    ) +
    (!isDailyReport
      ? sum(receiveRows, (r) => r.rwg?.amount) +
        sum(receiveRows, (r) => r.rws?.amount)
      : 0);
  const gvNormalCharges =
    normalChargeSections.reduce(
      (acc, k) => acc + sum(givenRows, (r) => r[k]?.charges),
      0,
    ) +
    (!isDailyReport
      ? sum(givenRows, (r) => r.rwg?.amount) +
        sum(givenRows, (r) => r.rws?.amount)
      : 0);

  // Sepcial lab and ginni
  const rxSpecialCharges =
    specialChargeSections.reduce(
      (acc, k) => acc + sum(receiveRows, (r) => r[k]?.charges),
      0,
    ) +
    (!isDailyReport
      ? sum(receiveRows, (r) => r.rwg?.amount) +
        sum(receiveRows, (r) => r.rws?.amount)
      : 0);
  const gvSpecialChages =
    specialChargeSections.reduce(
      (acc, k) => acc + sum(givenRows, (r) => r[k]?.charges),
      0,
    ) +
    (!isDailyReport
      ? sum(givenRows, (r) => r.rwg?.amount) +
        sum(givenRows, (r) => r.rws?.amount)
      : 0);

  const totalCharges =
    rxNormalCharges - gvNormalCharges + (rxSpecialCharges + gvSpecialChages);

  // Paid for both normal and sepcial ones

  const rxNormalPaid = normalChargeSections.reduce(
    (acc, k) => acc + sum(receiveRows, (r) => r[k]?.paidAmount),
    0,
  );

  const gvNormalPaid = normalChargeSections.reduce(
    (acc, k) => acc + sum(givenRows, (r) => r[k]?.paidAmount),
    0,
  );

  const rxSpecialPaid = specialChargeSections.reduce(
    (acc, k) => acc + sum(receiveRows, (r) => r[k]?.paidAmount),
    0,
  );

  const gvSpecialPaid = specialChargeSections.reduce(
    (acc, k) => acc + sum(givenRows, (r) => r[k]?.paidAmount),
    0,
  );

  const totalPaid =
    rxNormalPaid - gvNormalPaid + (rxSpecialPaid + gvSpecialPaid);

  const chargesNet = totalCharges - totalPaid; // positive = customer still owes; negative = we owe back
  const chargeDue = Math.max(0, chargesNet);
  const chargeBack = Math.max(0, -chargesNet);

  // ── Cash including charges ──
  // rawCashBalance > 0 → customer owes us cash (receivable)
  // chargesNet > 0     → customer owes us charges (receivable)
  // Combined: positive = receivable from customer
  const cashWithCharges = rawCashBalance + chargesNet;

  // ── Entry counts ──
  const customers = new Set([
    ...receiveRows.map((r) => r.party),
    ...givenRows.map((r) => r.party),
  ]);
  customers.delete("—");
  customers.delete("-");

  return {
    // gold
    goldBalance,
    goldDirection:
      goldBalance === 0
        ? "Settled"
        : goldBalance > 0
          ? "Payable"
          : "Receivable",
    rxGold,
    gvGold,

    // silver
    silverBalance,
    silverDirection:
      silverBalance === 0
        ? "Settled"
        : silverBalance >= 0
          ? "Payable"
          : "Receivable",
    rxSilver,
    gvSilver,

    // cash (with charges baked in)
    cashWithCharges,
    cashDirection:
      cashWithCharges === 0
        ? "Settled"
        : cashWithCharges > 0
          ? "Payable"
          : "Receivable",
    rxCash,
    gvCash,
    rawCashBalance,

    // charges breakdown (for detail section)
    totalCharges,
    totalPaid,
    chargeDue,
    chargeBack,
    // rxCharges,
    // gvCharges,

    // counts
    customerCount: customers.size,
    rxCount: receiveRows.length,
    gvCount: givenRows.length,
  };
}

/* ─────────────────────────────────────────
   LedgerStats
───────────────────────────────────────── */
function LedgerStats({ receiveRows, givenRows, isDailyReport = false }) {
  const [showStats, setShowStats] = useState(false);
  const [showDetails, setShowDetails] = useState(false); // charges + entries collapsed by default

  const stats = useMemo(
    () => computeStats(receiveRows, givenRows, isDailyReport),
    [receiveRows, givenRows, isDailyReport],
  );

  return (
    <>
      {/* toggle */}
      <div className="lt2-stats-toggle">
        <button
          className={`lt2-stats-btn ${showStats ? "active" : ""}`}
          onClick={() => setShowStats((p) => !p)}
        >
          <i className="fas fa-chart-pie" />
          {showStats ? "Hide Stats" : "Show Stats"}
        </button>
      </div>

      {showStats && (
        <StatsPanel
          stats={stats}
          isDailyReport={isDailyReport}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
        />
      )}
    </>
  );
}

export function LedgerStatsComputed({ stats, isDailyReport = false }) {
  const [showStats, setShowStats] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      {/* toggle */}
      <div className="lt2-stats-toggle">
        <button
          className={`lt2-stats-btn ${showStats ? "active" : ""}`}
          onClick={() => setShowStats((p) => !p)}
        >
          <i className="fas fa-chart-pie" />
          {showStats ? "Hide Stats" : "Show Stats"}
        </button>
      </div>

      {showStats && (
        <StatsPanel
          stats={stats || {}}
          isDailyReport={isDailyReport}
          showDetails={showDetails}
          setShowDetails={setShowDetails}
        />
      )}
    </>
  );
}
export default LedgerStats;

/* ─────────────────────────────────────────
   StatsPanel
───────────────────────────────────────── */
function StatsPanel({ stats, isDailyReport, showDetails, setShowDetails }) {
  const getDirection = (direction) => {
    switch (direction) {
      case "Receivable":
        return "fa-arrow-trend-up";
      case "Payable":
        return "fa-arrow-trend-down";
      default:
        return "fa-check-circle";
    }
  };
  const DirPill = ({ direction }) => (
    <span className={`ls-dir-pill ls-dir-${direction.toLowerCase()}`}>
      <i className={`fas ${getDirection(direction)}`} />
      {direction}
    </span>
  );

  return (
    <div className="ls-wrap">
      {/* ══ PRIMARY ROW: Gold / Silver / Cash (always visible) ══ */}
      <div className="ls-section-title">Summary</div>
      <div className="ls-grid ls-grid-3">
        {/* Gold */}
        <div className="ls-card ls-card-gold">
          <div className="ls-card-icon">
            <i className="fas fa-trophy" />
          </div>
          <div className="ls-card-body">
            <span className="ls-card-label">Gold</span>
            <span className="ls-card-value">
              {limitNumber(Math.abs(stats.goldBalance))} g
            </span>
            <DirPill direction={stats.goldDirection} />
            <div className="ls-card-sub">
              <span>
                <i className="fas fa-arrow-down ls-sub-rx" />{" "}
                {limitNumber(stats.rxGold)} g
              </span>
              <span>
                <i className="fas fa-arrow-up   ls-sub-gv" />{" "}
                {limitNumber(stats.gvGold)} g
              </span>
            </div>
          </div>
        </div>

        {/* Silver */}
        <div className="ls-card ls-card-silver">
          <div className="ls-card-icon">
            <i className="fas fa-circle-half-stroke" />
          </div>
          <div className="ls-card-body">
            <span className="ls-card-label">Silver</span>
            <span className="ls-card-value">
              {limitNumber(Math.abs(stats.silverBalance))} g
            </span>
            <DirPill direction={stats.silverDirection} />
            <div className="ls-card-sub">
              <span>
                <i className="fas fa-arrow-down ls-sub-rx" />{" "}
                {limitNumber(stats.rxSilver)} g
              </span>
              <span>
                <i className="fas fa-arrow-up   ls-sub-gv" />{" "}
                {limitNumber(stats.gvSilver)} g
              </span>
            </div>
          </div>
        </div>

        {/* Cash (includes charges net) */}
        <div className="ls-card ls-card-cash">
          <div className="ls-card-icon">
            <i className="fas fa-money-bill-wave" />
          </div>
          <div className="ls-card-body">
            <span className="ls-card-label">
              Cash
              <span className="ls-label-hint">(incl. charges)</span>
            </span>
            <span className="ls-card-value">
              {getAmountInCurrencyFormat(Math.abs(stats.cashWithCharges))}
            </span>
            <DirPill direction={stats.cashDirection} />
            <div className="ls-card-sub">
              <span title="Cash received">
                <i className="fas fa-arrow-down ls-sub-rx" />{" "}
                {getAmountInCurrencyFormat(stats.rxCash)}
              </span>
              <span title="Cash given">
                {" "}
                <i className="fas fa-arrow-up   ls-sub-gv" />{" "}
                {getAmountInCurrencyFormat(stats.gvCash)}
              </span>
              <span
                title="Charge balance"
                className={
                  stats.chargeDue > 0
                    ? "ls-sub-warn"
                    : stats.chargeBack > 0
                      ? "ls-sub-back"
                      : "ls-sub-ok"
                }
              >
                <i className="fas fa-file-invoice" />
                {stats.chargeDue > 0
                  ? `+${getAmountInCurrencyFormat(stats.chargeDue)} due`
                  : stats.chargeBack > 0
                    ? `-${getAmountInCurrencyFormat(stats.chargeBack)} back`
                    : "charges clear"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ══ DETAIL TOGGLE ══ */}
      <button
        className="ls-detail-toggle"
        onClick={() => setShowDetails((p) => !p)}
      >
        <i
          className={`fas fa-chevron-down ls-detail-chevron ${showDetails ? "ls-chevron-open" : ""}`}
        />
        {showDetails ? "Hide details" : "Show details"}
        {(stats.chargeDue > 0 || stats.chargeBack > 0) && !showDetails && (
          <span
            className={`ls-detail-alert ${stats.chargeBack > 0 ? "ls-alert-back" : "ls-alert-due"}`}
          >
            {stats.chargeBack > 0
              ? `${getAmountInCurrencyFormat(stats.chargeBack)} payable back`
              : `${getAmountInCurrencyFormat(stats.chargeDue)} due`}
          </span>
        )}
      </button>

      {/* ══ COLLAPSIBLE DETAIL: Charges + Entries ══ */}
      {showDetails && (
        <div className="ls-details-wrap">
          {/* Charges row */}
          <div className="ls-section-title" style={{ marginTop: 12 }}>
            Charges{" "}
            {!isDailyReport && (
              <span className="ls-title-dim">incl. RWG / RWS</span>
            )}
          </div>
          <div className="ls-grid ls-grid-4">
            <div className="ls-card ls-card-neutral">
              <div className="ls-card-icon">
                <i className="fas fa-file-invoice" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Total Charges</span>
                <span className="ls-card-value">
                  {getAmountInCurrencyFormat(stats.totalCharges)}
                </span>
                {/* <div className="ls-card-sub">
                  <span>
                    <i className="fas fa-arrow-down ls-sub-rx" />{" "}
                    {getAmountInCurrencyFormat(stats.rxCharges)}
                  </span>
                  <span>
                    <i className="fas fa-arrow-up   ls-sub-gv" />{" "}
                    {getAmountInCurrencyFormat(stats.gvCharges)}
                  </span>
                </div> */}
              </div>
            </div>

            <div className="ls-card ls-card-paid">
              <div className="ls-card-icon">
                <i className="fas fa-hand-holding-dollar" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Total Paid</span>
                <span className="ls-card-value">
                  {getAmountInCurrencyFormat(stats.totalPaid)}
                </span>
              </div>
            </div>

            <div
              className={`ls-card ${stats.chargeDue > 0 ? "ls-card-due" : "ls-card-neutral-dim"}`}
            >
              <div className="ls-card-icon">
                <i className="fas fa-clock" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Due</span>
                <span className="ls-card-value">
                  {stats.chargeDue > 0
                    ? getAmountInCurrencyFormat(stats.chargeDue)
                    : "—"}
                </span>
              </div>
            </div>

            <div
              className={`ls-card ${stats.chargeBack > 0 ? "ls-card-payback" : "ls-card-neutral-dim"}`}
            >
              <div className="ls-card-icon">
                <i className="fas fa-rotate-left" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Payable Back</span>
                <span className="ls-card-value">
                  {stats.chargeBack > 0
                    ? getAmountInCurrencyFormat(stats.chargeBack)
                    : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Entries row */}
          <div className="ls-section-title" style={{ marginTop: 14 }}>
            Entries
          </div>
          <div className="ls-grid ls-grid-3">
            {stats.customerCount > 0 && (
              <div className="ls-card ls-card-neutral">
                <div className="ls-card-icon">
                  <i className="fas fa-users" />
                </div>
                <div className="ls-card-body">
                  <span className="ls-card-label">Customers</span>
                  <span className="ls-card-value">{stats.customerCount}</span>
                </div>
              </div>
            )}

            <div className="ls-card ls-card-rx">
              <div className="ls-card-icon">
                <i className="fas fa-list-check" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Receive Entries</span>
                <span className="ls-card-value">{stats.rxCount}</span>
              </div>
            </div>

            <div className="ls-card ls-card-gv">
              <div className="ls-card-icon">
                <i className="fas fa-list" />
              </div>
              <div className="ls-card-body">
                <span className="ls-card-label">Given Entries</span>
                <span className="ls-card-value">{stats.gvCount}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
